import { QueryClient, QueryCache, MutationCache } from '@tanstack/react-query';
import type { ApiError } from '../types';

// Create a custom error handler for queries
const handleQueryError = (error: unknown) => {
  const apiError = error && typeof error === 'object' && 'status' in error 
    ? error as ApiError 
    : { status: 0, message: 'Unknown error', details: error };
  
  // Log error for debugging
  console.error('Query error:', apiError);
  
  // Handle authentication errors globally
  if (apiError.status === 401) {
    // Emit auth error event for AuthContext to handle
    window.dispatchEvent(new CustomEvent('auth-error'));
  }
  
  // Handle network errors
  if (apiError.status === 0) {
    // Could emit a network error event here
    console.warn('Network error detected');
  }
};

// Create query cache with error handling
const queryCache = new QueryCache({
  onError: handleQueryError,
});

// Create mutation cache with error handling
const mutationCache = new MutationCache({
  onError: handleQueryError,
});

// Create and configure the query client with optimized settings
export const queryClient = new QueryClient({
  queryCache,
  mutationCache,
  defaultOptions: {
    queries: {
      // Stale time: how long data is considered fresh (2 minutes - reduced for better memory)
      staleTime: 2 * 60 * 1000,
      
      // Cache time: how long inactive data stays in cache (5 minutes - reduced to prevent memory buildup)
      gcTime: 5 * 60 * 1000,
      
      // Retry configuration
      retry: (failureCount, error) => {
        const apiError = error && typeof error === 'object' && 'status' in error 
          ? error as ApiError 
          : { status: 0, message: 'Unknown error', details: error };
        
        // Don't retry on authentication errors
        if (apiError.status === 401 || apiError.status === 403) {
          return false;
        }
        
        // Don't retry on client errors (4xx)
        if (apiError.status >= 400 && apiError.status < 500) {
          return false;
        }
        
        // Retry up to 3 times for server errors and network issues
        return failureCount < 3;
      },
      
      // Retry delay with exponential backoff
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      
      // Refetch on window focus (can be disabled per query)
      refetchOnWindowFocus: true,
      
      // Refetch on reconnect
      refetchOnReconnect: true,
      
      // Background refetch interval (disabled by default, can be enabled per query)
      refetchInterval: false,
    },
    mutations: {
      // Retry mutations once on network errors
      retry: (failureCount, error) => {
        const apiError = error && typeof error === 'object' && 'status' in error 
          ? error as ApiError 
          : { status: 0, message: 'Unknown error', details: error };
        
        // Only retry on network errors
        if (apiError.status === 0 && failureCount < 1) {
          return true;
        }
        
        return false;
      },
      
      // Retry delay for mutations
      retryDelay: 1000,
    },
  },
});

// Query key factory for consistent key generation
export const queryKeys = {
  // Auth keys
  auth: {
    currentUser: ['auth', 'currentUser'] as const,
  },
  
  // Holiday keys
  holidays: {
    all: ['holidays'] as const,
    lists: () => [...queryKeys.holidays.all, 'list'] as const,
    list: (params?: Record<string, any>) => [...queryKeys.holidays.lists(), params] as const,
    details: () => [...queryKeys.holidays.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.holidays.details(), id] as const,
    search: (query: string) => [...queryKeys.holidays.all, 'search', query] as const,
  },
  
  // User keys
  users: {
    all: ['users'] as const,
    lists: () => [...queryKeys.users.all, 'list'] as const,
    list: (params?: Record<string, any>) => [...queryKeys.users.lists(), params] as const,
    details: () => [...queryKeys.users.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.users.details(), id] as const,
  },
  
  // Analytics keys
  analytics: {
    all: ['analytics'] as const,
    dashboard: () => [...queryKeys.analytics.all, 'dashboard'] as const,
    holidays: (dateRange?: { start: string; end: string }) => 
      [...queryKeys.analytics.all, 'holidays', dateRange] as const,
    users: (dateRange?: { start: string; end: string }) => 
      [...queryKeys.analytics.all, 'users', dateRange] as const,
  },
  
  // Reports keys
  reports: {
    all: ['reports'] as const,
    history: () => [...queryKeys.reports.all, 'history'] as const,
  },
} as const;

// Utility functions for cache management
export const cacheUtils = {
  // Invalidate all queries for a specific entity
  invalidateHolidays: () => {
    return queryClient.invalidateQueries({ queryKey: queryKeys.holidays.all });
  },
  
  invalidateUsers: () => {
    return queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
  },
  
  invalidateAnalytics: () => {
    return queryClient.invalidateQueries({ queryKey: queryKeys.analytics.all });
  },
  
  // Remove specific queries from cache
  removeHoliday: (id: number) => {
    queryClient.removeQueries({ queryKey: queryKeys.holidays.detail(id) });
  },
  
  removeUser: (id: number) => {
    queryClient.removeQueries({ queryKey: queryKeys.users.detail(id) });
  },
  
  // Update cached data optimistically
  updateHolidayCache: (id: number, updater: (old: any) => any) => {
    queryClient.setQueryData(queryKeys.holidays.detail(id), updater);
  },
  
  updateUserCache: (id: number, updater: (old: any) => any) => {
    queryClient.setQueryData(queryKeys.users.detail(id), updater);
  },
  
  // Prefetch data
  prefetchHoliday: (id: number) => {
    return queryClient.prefetchQuery({
      queryKey: queryKeys.holidays.detail(id),
      queryFn: () => import('../services/holidayService').then(({ holidayService }) => 
        holidayService.getHoliday(id)
      ),
    });
  },
  
  prefetchUser: (id: number) => {
    return queryClient.prefetchQuery({
      queryKey: queryKeys.users.detail(id),
      queryFn: () => import('../services/userService').then(({ userService }) => 
        userService.getUser(id)
      ),
    });
  },
  
  // Clear all cache
  clearAll: () => {
    queryClient.clear();
  },
  
  // Get cache stats
  getCacheStats: () => {
    const cache = queryClient.getQueryCache();
    return {
      totalQueries: cache.getAll().length,
      activeQueries: cache.getAll().filter(query => query.getObserversCount() > 0).length,
      staleQueries: cache.getAll().filter(query => query.isStale()).length,
    };
  },
  
  // Cleanup stale and inactive queries to prevent memory leaks
  cleanupStaleQueries: () => {
    const cache = queryClient.getQueryCache();
    const queries = cache.getAll();
    
    queries.forEach(query => {
      // Remove queries with no observers that are stale
      if (query.getObserversCount() === 0 && query.isStale()) {
        cache.remove(query);
      }
    });
  },
  
  // Get memory usage estimate (approximate)
  getMemoryEstimate: () => {
    const cache = queryClient.getQueryCache();
    const queries = cache.getAll();
    
    let totalSize = 0;
    queries.forEach(query => {
      const data = query.state.data;
      if (data) {
        // Rough estimate: stringify and measure length
        try {
          totalSize += JSON.stringify(data).length;
        } catch {
          // Skip if data can't be stringified
        }
      }
    });
    
    return {
      estimatedBytes: totalSize,
      estimatedKB: Math.round(totalSize / 1024),
      estimatedMB: Math.round(totalSize / (1024 * 1024) * 100) / 100,
    };
  },
};

// Network status detection
export const networkUtils = {
  // Check if online
  isOnline: () => navigator.onLine,
  
  // Setup online/offline listeners
  setupNetworkListeners: () => {
    const handleOnline = () => {
      queryClient.resumePausedMutations();
      queryClient.invalidateQueries();
    };
    
    const handleOffline = () => {
      // Queries will automatically pause when offline
      console.log('Application is offline');
    };
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  },
};

export default queryClient;